import React from "react";
import { Form, Formik, Field } from "formik";
import { Card, CardBody, FormGroup, Input, Label } from "reactstrap";
import { Loader } from "react-bootstrap-typeahead";
import { useAfasDebugMode } from "Pages/Coordinator/Afas/hooks/useAfasDebugMode";

export const isAfasMandatoryComplete = (data, connection = {}) => {
    if (
        !String(data?.environment ?? "").trim() ||
        !String(data?.werkgever_id ?? "").trim()
    ) {
        return false;
    }

    const hasExistingToken = Boolean(connection?.has_token);
    if (!hasExistingToken && !String(data?.token ?? "").trim()) {
        return false;
    }

    return true;
};

/** Keep stored DB values for existing links; never auto-enable for new/incomplete forms. */
export const resolveConnectorSwitchValue = (
    storedValue,
    mandatoryComplete,
) => {
    if (!mandatoryComplete) {
        return false;
    }

    // Configured bestuur: preserve exact DB value.
    // Missing value for a new link → stay off until the user enables it.
    return Boolean(storedValue);
};

const emptyValues = {
    environment: "",
    configured_on_environment: "",
    token: "",
    clooser_verzoek_guid: "",
    clooser_regel_guid: "",
    werkgever_id: "",
    werkgever_naam: "",
    get_connectors_enabled: false,
    update_connectors_enabled: false,
};

const AfasKoppelingFormSection = ({ title, children }) => (
    <Card className="main-card mb-4">
        <CardBody className="d-flex flex-column">
            <h6 className="mb-4 fw-semibold">{title}</h6>
            {children}
        </CardBody>
    </Card>
);

const HorizontalField = ({ label, htmlFor, children }) => (
    <FormGroup className="form-element d-flex justify-content-between align-items-center mb-3">
        <Label for={htmlFor} className="mb-0 col-5">
            {label}
        </Label>
        <div className="col-6">{children}</div>
    </FormGroup>
);

const ConnectorSwitch = ({ id, name, label, checked, disabled, onChange }) => (
    <div className="d-flex justify-content-between custom-switch mb-3">
        <div style={{ width: "60%" }}>
            <Label for={id} className={`mb-0${disabled ? " text-muted" : ""}`}>
                {label}
            </Label>
        </div>
        <div style={{ width: "40%" }}>
            <label className={`switch${disabled ? " opacity-50" : ""}`}>
                <Field
                    id={id}
                    type="checkbox"
                    name={name}
                    checked={checked}
                    disabled={disabled}
                    onChange={onChange}
                />
                <span className="slider round"></span>
            </label>
        </div>
    </div>
);

/** Standalone toggle (not part of Formik / save payload). */
const DebugModeSwitch = ({ id, label, checked, onChange }) => (
    <div className="d-flex justify-content-between custom-switch mb-0">
        <div style={{ width: "60%" }}>
            <Label for={id} className="mb-0">
                {label}
            </Label>
        </div>
        <div style={{ width: "40%" }}>
            <label className="switch">
                <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    onChange={onChange}
                />
                <span className="slider round"></span>
            </label>
        </div>
    </div>
);

const AfasKoppelingFormBody = ({
    values,
    handleChange,
    setFieldValue,
    connectionData,
    onFieldChange,
    health,
    healthLabel,
    debugMode,
    onDebugModeChange,
}) => {
    // UI-only guard: do not mutate form/DB values for existing AFAS links.
    // When mandatory fields are incomplete, switches look off and stay disabled.
    const connectorsAllowed = isAfasMandatoryComplete(
        {
            environment: values.environment,
            werkgever_id: values.werkgever_id,
            token: values.token,
        },
        connectionData,
    );

    return (
        <Form>
            {health && (
                <p className="text-muted mb-3">
                    Status:{" "}
                    <span
                        className={
                            health.reachable
                                ? "text-success"
                                : "text-warning"
                        }
                    >
                        {healthLabel}
                    </span>
                </p>
            )}

            <AfasKoppelingFormSection title="Debug">
                <DebugModeSwitch
                    id="afas_debug_mode"
                    label="Debugmodus"
                    checked={debugMode}
                    onChange={(e) => onDebugModeChange(e.target.checked)}
                />
                <p className="text-muted small mb-0 mt-2">
                    Alleen lezen: toont AFAS-debuggegevens zonder wijzigingen te
                    versturen naar connectors.
                </p>
                {debugMode && (
                    <p className="text-info small mb-0 mt-2">
                        Debugmodus is ingeschakeld. Debugpanelen volgen in een
                        volgende stap.
                    </p>
                )}
            </AfasKoppelingFormSection>

            <AfasKoppelingFormSection title="Afas App connector gegevens">
                <HorizontalField label="Omgeving" htmlFor="environment">
                    <Input
                        id="environment"
                        name="environment"
                        type="text"
                        value={values.environment}
                        onChange={(e) => {
                            handleChange(e);
                            onFieldChange("environment", e.target.value);
                        }}
                    />
                </HorizontalField>

                <HorizontalField
                    label="Ingesteld op omgeving"
                    htmlFor="configured_on_environment"
                >
                    <Input
                        id="configured_on_environment"
                        name="configured_on_environment"
                        type="text"
                        value={values.configured_on_environment}
                        onChange={(e) => {
                            handleChange(e);
                            onFieldChange(
                                "configured_on_environment",
                                e.target.value
                            );
                        }}
                    />
                </HorizontalField>

                <HorizontalField label="AFAS-token" htmlFor="token">
                    <Input
                        id="token"
                        name="token"
                        type="text"
                        autoComplete="off"
                        placeholder="AFAS-token"
                        value={values.token}
                        onChange={(e) => {
                            handleChange(e);
                            onFieldChange("token", e.target.value);
                        }}
                    />
                </HorizontalField>
            </AfasKoppelingFormSection>

            <AfasKoppelingFormSection title="Connectors">
                <ConnectorSwitch
                    id="get_connectors_enabled"
                    name="get_connectors_enabled"
                    label="Get connectors"
                    checked={
                        connectorsAllowed && values.get_connectors_enabled
                    }
                    disabled={!connectorsAllowed}
                    onChange={(e) => {
                        if (!connectorsAllowed) {
                            return;
                        }
                        const checked = e.target.checked;
                        setFieldValue("get_connectors_enabled", checked);
                        onFieldChange("get_connectors_enabled", checked);
                    }}
                />
                <ConnectorSwitch
                    id="update_connectors_enabled"
                    name="update_connectors_enabled"
                    label="Update connectors"
                    checked={
                        connectorsAllowed &&
                        values.update_connectors_enabled
                    }
                    disabled={!connectorsAllowed}
                    onChange={(e) => {
                        if (!connectorsAllowed) {
                            return;
                        }
                        const checked = e.target.checked;
                        setFieldValue("update_connectors_enabled", checked);
                        onFieldChange("update_connectors_enabled", checked);
                    }}
                />
            </AfasKoppelingFormSection>

            <AfasKoppelingFormSection title="Afas-Guids vrije velden">
                <HorizontalField
                    label="ClooserVerzoekId"
                    htmlFor="clooser_verzoek_guid"
                >
                    <Input
                        id="clooser_verzoek_guid"
                        name="clooser_verzoek_guid"
                        type="text"
                        value={values.clooser_verzoek_guid}
                        onChange={(e) => {
                            handleChange(e);
                            onFieldChange(
                                "clooser_verzoek_guid",
                                e.target.value
                            );
                        }}
                    />
                </HorizontalField>

                <HorizontalField
                    label="ClooserRegelId"
                    htmlFor="clooser_regel_guid"
                >
                    <Input
                        id="clooser_regel_guid"
                        name="clooser_regel_guid"
                        type="text"
                        value={values.clooser_regel_guid}
                        onChange={(e) => {
                            handleChange(e);
                            onFieldChange(
                                "clooser_regel_guid",
                                e.target.value
                            );
                        }}
                    />
                </HorizontalField>
            </AfasKoppelingFormSection>

            <AfasKoppelingFormSection title="Overige Afas velden">
                <HorizontalField
                    label="Werkgevercode"
                    htmlFor="werkgever_id"
                >
                    <Input
                        id="werkgever_id"
                        name="werkgever_id"
                        type="text"
                        value={values.werkgever_id}
                        onChange={(e) => {
                            handleChange(e);
                            onFieldChange("werkgever_id", e.target.value);
                        }}
                    />
                </HorizontalField>

                <HorizontalField
                    label="Werkgever naam"
                    htmlFor="werkgever_naam"
                >
                    <Input
                        id="werkgever_naam"
                        name="werkgever_naam"
                        type="text"
                        value={values.werkgever_naam}
                        onChange={(e) => {
                            handleChange(e);
                            onFieldChange("werkgever_naam", e.target.value);
                        }}
                    />
                </HorizontalField>
            </AfasKoppelingFormSection>
        </Form>
    );
};

const AfasKoppeling = ({
    isLoading,
    connectionData,
    onFieldChange,
    communityId: communityIdProp,
}) => {
    const communityId =
        communityIdProp ?? connectionData?.iCommunityId ?? null;

    const [debugMode, setDebugMode] = useAfasDebugMode(communityId);

    const mandatoryComplete = isAfasMandatoryComplete(
        {
            environment: connectionData?.environment,
            werkgever_id: connectionData?.werkgever_id,
            token: connectionData?.token,
        },
        connectionData,
    );

    const initialValues = {
        environment: connectionData?.environment ?? "",
        configured_on_environment:
            connectionData?.configured_on_environment ?? "",
        token: connectionData?.token ?? "",
        clooser_verzoek_guid: connectionData?.clooser_verzoek_guid ?? "",
        clooser_regel_guid: connectionData?.clooser_regel_guid ?? "",
        werkgever_id: connectionData?.werkgever_id ?? "",
        werkgever_naam: connectionData?.werkgever_naam ?? "",
        get_connectors_enabled: resolveConnectorSwitchValue(
            connectionData?.get_connectors_enabled,
            mandatoryComplete,
        ),
        update_connectors_enabled: resolveConnectorSwitchValue(
            connectionData?.update_connectors_enabled,
            mandatoryComplete,
        ),
    };

    if (isLoading) {
        return (
            <Card className="main-card">
                <CardBody className="d-flex flex-column">
                    <Loader
                        type="ball-grid-pulse"
                        className="mt-3 d-flex mx-auto justify-content-center"
                    />
                </CardBody>
            </Card>
        );
    }

    const health = connectionData?.health;
    const healthLabel = health?.reachable
        ? `Verbonden${health?.profit_version ? ` (Profit ${health.profit_version})` : ""}`
        : health?.configured
          ? "Geconfigureerd, niet bereikbaar"
          : "Niet geconfigureerd";

    return (
        <Formik enableReinitialize initialValues={initialValues}>
            {({ values, handleChange, setFieldValue }) => (
                <AfasKoppelingFormBody
                    values={values}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    connectionData={connectionData}
                    onFieldChange={onFieldChange}
                    health={health}
                    healthLabel={healthLabel}
                    debugMode={debugMode}
                    onDebugModeChange={setDebugMode}
                />
            )}
        </Formik>
    );
};

export { emptyValues };
export default AfasKoppeling;
